// src/pages/ProfilePage.tsx
import React, { useState, useEffect } from 'react';
import { Box, Chip, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useAuth } from '../contexts/AuthContext';
import { useAppTheme } from '../contexts/ThemeContext';
import { useLocale, AVAILABLE_LOCALES } from '../contexts/LocaleContext';
import { userPreferencesAPI, type MessagePreferences } from '../services/api';
import NotificationsInbox from '../components/notifications/NotificationsInbox';
import { useNotifications } from '../hooks/useNotifications';
import { useTabQueryParam } from '../hooks/useTabQueryParam';
import { useGlobalToast } from '../App';
import PageTitle from '../components/common/PageTitle';
import { ProfilePageSubMenu } from '../components/menus';
import { StyledSwitch } from '../components/forms/shared/FormElements';
import CustomSelect from '../components/common/CustomSelect';
import CustomSelectMenuItem from '../components/common/CustomSelectMenuItem';
import PasswordField from '../components/common/PasswordField';
import { SaveFormButton } from '../components/common/Buttons';
import { InlineMessage } from '../components/common/InlineMessage';
import {
  SettingsBlock,
  SettingsCard,
  SectionLabel,
  SettingRow,
  SettingLabel,
  SettingDescription,
  SectionDivider,
  Section,
} from '../components/settings/shared/SystemSettingsElements';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`profile-tabpanel-${index}`}
    aria-labelledby={`profile-tab-${index}`}
  >
    {value === index && <Box>{children}</Box>}
  </div>
);

const MainContainer = styled(Box)({ position: 'relative' });

const PageContainer = styled(Box)({
  margin: '0 auto',
  maxWidth: '100%',
  padding: '0',
});

const FieldRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: '1rem',
}));

const PROFILE_TAB_IDS = ['account', 'notifications'] as const;
type ProfileTabId = (typeof PROFILE_TAB_IDS)[number];

const ProfilePage: React.FC = () => {
  const { user, updateUserPrefs } = useAuth();
  const { themeMode, toggleTheme } = useAppTheme();
  const { locale, setLocale } = useLocale();
  const { showSuccess, showError } = useGlobalToast();
  const { unreadCount } = useNotifications();

  const [tabId, setTabId] = useTabQueryParam<ProfileTabId>(
    'tab',
    PROFILE_TAB_IDS,
    'account'
  );
  const tab = tabId === 'notifications' ? 1 : 0;

  const [prefs, setPrefs] = useState<MessagePreferences>({
    msgBackupAlerts: true,
    msgQuoteAlerts: true,
    msgBookingReminders: true,
    msgSystemToasts: true,
    accessibilityMode: false,
    showPerformanceIndicator: true,
    showOnboarding: true,
    themeMode: 'light',
    locale: 'en-ZA',
  });
  const [savingPrefs, setSavingPrefs] = useState(false);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);

  useEffect(() => {
    userPreferencesAPI
      .getMessagePreferences()
      .then(setPrefs)
      .catch(() => {});
  }, []);

  const handleTabChange = (newValue: number) => {
    setTabId(newValue === 1 ? 'notifications' : 'account');
  };

  const handlePrefChange = async (
    key: keyof MessagePreferences,
    value: boolean
  ) => {
    const updated = { ...prefs, [key]: value };
    setPrefs(updated);
    setSavingPrefs(true);
    // Theme and language are changed outside this page's local copy, so the
    // live user values win over the snapshot loaded on mount.
    const payload: MessagePreferences = {
      ...updated,
      themeMode: user?.themeMode ?? updated.themeMode,
      locale: user?.locale ?? updated.locale,
    };
    try {
      await userPreferencesAPI.updateMessagePreferences(payload);
      updateUserPrefs(payload);
      showSuccess();
    } catch {
      showError();
      setPrefs(prefs);
    } finally {
      setSavingPrefs(false);
    }
  };

  const passwordsMatch = newPassword === confirmNewPassword;
  const canChangePassword =
    currentPassword.length > 0 && newPassword.length > 0 && passwordsMatch;

  const handleChangePassword = async () => {
    setPasswordError(null);
    setChangingPassword(true);
    try {
      await userPreferencesAPI.changePassword(currentPassword, newPassword);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmNewPassword('');
      showSuccess();
    } catch (err: any) {
      setPasswordError(
        err?.response?.data?.message || 'Failed to change password'
      );
    } finally {
      setChangingPassword(false);
    }
  };

  return (
    <MainContainer>
      <PageTitle title="Profile" showSeparator={false} modeText="" />

      <ProfilePageSubMenu
        selectedTab={tab}
        setSelectedTab={handleTabChange}
        unreadCount={unreadCount}
        bottomBorder={true}
        buttonBottomBorder={false}
      />

      <PageContainer>
        <TabPanel value={tab} index={0}>
          <SettingsBlock>
            <SettingsCard elevation={0}>
              <Section>
                <SectionLabel>Account</SectionLabel>

                <FieldRow>
                  <SettingLabel>Username</SettingLabel>
                  <Typography variant="body1" fontWeight={600}>
                    {user?.username}
                  </Typography>
                </FieldRow>

                <SectionDivider sx={{ mx: 0, my: '0.75rem' }} />

                <FieldRow>
                  <SettingLabel>Role</SettingLabel>
                  <Chip label={user?.role} size="small" />
                </FieldRow>
              </Section>

              <SectionDivider />

              <Section>
                <SectionLabel>Change Password</SectionLabel>
                <SettingDescription sx={{ mb: '1rem' }}>
                  Enter your current password to set a new one.
                </SettingDescription>

                {passwordError && (
                  <InlineMessage message={passwordError} severity="error" />
                )}

                <Box display="flex" flexDirection="column" gap={1}>
                  <PasswordField
                    fullWidth
                    label="Current Password"
                    variant="outlined"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    autoComplete="current-password"
                    disabled={changingPassword}
                  />
                  <PasswordField
                    fullWidth
                    label="New Password"
                    variant="outlined"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    autoComplete="new-password"
                    disabled={changingPassword}
                  />
                  <PasswordField
                    fullWidth
                    label="Confirm New Password"
                    variant="outlined"
                    value={confirmNewPassword}
                    onChange={(e) => setConfirmNewPassword(e.target.value)}
                    autoComplete="new-password"
                    disabled={changingPassword}
                    error={confirmNewPassword.length > 0 && !passwordsMatch}
                    helperText={
                      confirmNewPassword.length > 0 && !passwordsMatch
                        ? 'Passwords do not match'
                        : ''
                    }
                  />
                  <Box>
                    <SaveFormButton
                      text="Update Password"
                      isSubmitting={changingPassword}
                      disabled={!canChangePassword}
                      onClick={handleChangePassword}
                    />
                  </Box>
                </Box>
              </Section>

              <SectionDivider />

              <Section>
                <SectionLabel>Notification Preferences</SectionLabel>
                <SettingDescription sx={{ mb: '1rem' }}>
                  System notifications are always delivered. Toggle the
                  categories below to control what else you receive, and whether
                  System messages also pop up as an instant toast.
                </SettingDescription>

                <SettingRow>
                  <Box>
                    <SettingLabel>Backup alerts</SettingLabel>
                  </Box>
                  <StyledSwitch
                    checked={prefs.msgBackupAlerts}
                    onChange={(e) =>
                      handlePrefChange('msgBackupAlerts', e.target.checked)
                    }
                    disabled={savingPrefs}
                  />
                </SettingRow>

                <SectionDivider sx={{ mx: 0, my: '0.5rem' }} />

                <SettingRow>
                  <Box>
                    <SettingLabel>Quote alerts</SettingLabel>
                  </Box>
                  <StyledSwitch
                    checked={prefs.msgQuoteAlerts}
                    onChange={(e) =>
                      handlePrefChange('msgQuoteAlerts', e.target.checked)
                    }
                    disabled={savingPrefs}
                  />
                </SettingRow>

                <SectionDivider sx={{ mx: 0, my: '0.5rem' }} />

                <SettingRow>
                  <Box>
                    <SettingLabel>Booking reminders</SettingLabel>
                  </Box>
                  <StyledSwitch
                    checked={prefs.msgBookingReminders}
                    onChange={(e) =>
                      handlePrefChange('msgBookingReminders', e.target.checked)
                    }
                    disabled={savingPrefs}
                  />
                </SettingRow>

                <SectionDivider sx={{ mx: 0, my: '0.5rem' }} />

                <SettingRow>
                  <Box>
                    <SettingLabel>
                      Instant toast for System messages
                    </SettingLabel>
                  </Box>
                  <StyledSwitch
                    checked={prefs.msgSystemToasts}
                    onChange={(e) =>
                      handlePrefChange('msgSystemToasts', e.target.checked)
                    }
                    disabled={savingPrefs}
                  />
                </SettingRow>
              </Section>

              <SectionDivider />

              <Section>
                <SectionLabel>Display Preferences</SectionLabel>
                <SettingDescription sx={{ mb: '1rem' }}>
                  These only affect your own account, not other users.
                </SettingDescription>

                <SettingRow>
                  <Box>
                    <SettingLabel>Accessibility Mode</SettingLabel>
                    <SettingDescription>
                      Bolder hover indication on inputs, dropdowns, radio
                      buttons and switches for low-vision mouse navigation
                    </SettingDescription>
                  </Box>
                  <StyledSwitch
                    checked={prefs.accessibilityMode}
                    onChange={(e) =>
                      handlePrefChange('accessibilityMode', e.target.checked)
                    }
                    disabled={savingPrefs}
                  />
                </SettingRow>

                <SectionDivider sx={{ mx: 0, my: '0.5rem' }} />

                <SettingRow>
                  <Box>
                    <SettingLabel>Show Performance Indicator</SettingLabel>
                    <SettingDescription>
                      Display the real-time performance badge on all pages
                    </SettingDescription>
                  </Box>
                  <StyledSwitch
                    checked={prefs.showPerformanceIndicator}
                    onChange={(e) =>
                      handlePrefChange(
                        'showPerformanceIndicator',
                        e.target.checked
                      )
                    }
                    disabled={savingPrefs}
                  />
                </SettingRow>

                <SectionDivider sx={{ mx: 0, my: '0.5rem' }} />

                <SettingRow>
                  <Box>
                    <SettingLabel>Show Onboarding</SettingLabel>
                    <SettingDescription>
                      Display onboarding hints for new bookings
                    </SettingDescription>
                  </Box>
                  <StyledSwitch
                    checked={prefs.showOnboarding}
                    onChange={(e) =>
                      handlePrefChange('showOnboarding', e.target.checked)
                    }
                    disabled={savingPrefs}
                  />
                </SettingRow>

                <SectionDivider sx={{ mx: 0, my: '0.5rem' }} />

                <SettingRow>
                  <Box>
                    <SettingLabel>Dark Mode</SettingLabel>
                    <SettingDescription>
                      Also available from the profile menu in the header
                    </SettingDescription>
                  </Box>
                  <StyledSwitch
                    checked={themeMode === 'dark'}
                    onChange={toggleTheme}
                  />
                </SettingRow>

                <SectionDivider sx={{ mx: 0, my: '0.5rem' }} />

                <SettingRow>
                  <Box>
                    <SettingLabel>Language</SettingLabel>
                    <SettingDescription>
                      Choose your preferred display language
                    </SettingDescription>
                  </Box>
                  <CustomSelect
                    value={locale}
                    onChange={(value) => setLocale(value)}
                    label="Language"
                  >
                    {AVAILABLE_LOCALES.map((l) => (
                      <CustomSelectMenuItem key={l.code} value={l.code}>
                        {l.label}
                      </CustomSelectMenuItem>
                    ))}
                  </CustomSelect>
                </SettingRow>
              </Section>
            </SettingsCard>
          </SettingsBlock>
        </TabPanel>

        <TabPanel value={tab} index={1}>
          <SettingsBlock>
            <SettingsCard elevation={0}>
              <Section>
                <NotificationsInbox />
              </Section>
            </SettingsCard>
          </SettingsBlock>
        </TabPanel>
      </PageContainer>
    </MainContainer>
  );
};

export default ProfilePage;
